import React from 'react';
import { useNavigate } from 'react-router-dom';

import { BrowserLogin } from '../../features/browserLogin/BrowserLogin';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { appActions } from '../../store/slices/appSlice';
import { bootstrapApp, bootstrapData } from '../../store/thunks';
import { deepLinkToPath, parseDeepLink } from '../../telegram/deepLink';
import { isTelegramEnvironment } from '../../telegram/telegram';
import { TavernButton } from '../../ui/components';

import './WelcomePage.css';

/**
 * Welcome Page - Tavern Entrance
 * First screen with club rules in a parchment scroll style
 */
export function WelcomePage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const rulesAccepted = useAppSelector((s) => s.app.rulesAccepted);
  const isLoading = useAppSelector((s) => s.app.isLoading);
  const bootstrapError = useAppSelector((s) => s.app.bootstrapError);
  const user = useAppSelector((s) => s.auth.user);

  const deepLinkTarget = React.useRef(deepLinkToPath(parseDeepLink()));
  const defaultRoute = deepLinkTarget.current ?? '/floor-plan';

  // Outside Telegram (regular browser) there is no initData: the user signs in via BrowserLogin.
  const [inTelegram] = React.useState(isTelegramEnvironment);
  const [sessionChecked, setSessionChecked] = React.useState(false);
  const showBrowserLogin = !user && !inTelegram && sessionChecked && !isLoading;

  // Preload session + initial data while showing welcome
  React.useEffect(() => {
    if (user) return;
    void dispatch(bootstrapData()).then(() => setSessionChecked(true));
  }, [dispatch, user]);

  // Auto-navigate if rules already accepted
  React.useEffect(() => {
    if (rulesAccepted && user) {
      navigate(defaultRoute, { replace: true });
    }
  }, [navigate, rulesAccepted, user, defaultRoute]);

  const handleAccept = async () => {
    try {
      if (!rulesAccepted) {
        if (user) {
          dispatch(appActions.setRulesAccepted(true));
        } else {
          await dispatch(bootstrapApp()).unwrap();
        }
      } else if (!user) {
        await dispatch(bootstrapData()).unwrap();
      }
      navigate(defaultRoute, { replace: true });
    } catch {
      // NotificationBar shows the error
    }
  };

  return (
    <div className="welcome-page">
      <div className="welcome-container">
        <div className="logo-container">
          <div className="logo" style={{ backgroundImage: 'url(/logo-with-text.png)' }}></div>
          <h1 className="welcome-title">Клуб &quot;Тропа&quot;</h1>
          <p className="welcome-subtitle">Гильдия искателей приключений</p>
        </div>

        {showBrowserLogin && <BrowserLogin onLoggedIn={() => void dispatch(bootstrapData())} />}

        {bootstrapError && (
          <div role="alert" className="welcome-error-alert">
            {bootstrapError}
          </div>
        )}

      {!showBrowserLogin && (
      <>
      <div className="rules-scroll">
        <h2 className="rules-title">📜 Правила Клуба</h2>

        <div className="rule-item">
          <span className="rule-number">1.</span>
          Бережно относиться к оборудованию и помещениям клуба
        </div>

        <div className="rule-item">
          <span className="rule-number">2.</span>
          Соблюдать забронированное время использования ресурсов
        </div>

        <div className="rule-item">
          <span className="rule-number">3.</span>
          Уважительно относиться к другим участникам
        </div>

        <div className="rule-item">
          <span className="rule-number">4.</span>
          Своевременно отменять бронирования, если не можете прийти
        </div>

        <div className="rule-item">
          <span className="rule-number">5.</span>
          Поддерживать чистоту и порядок в помещениях
        </div>

        <div className="rule-item">
          <span className="rule-number">6.</span>
          Запрещено курение на территории клуба
        </div>

        <div className="rule-item">
          <span className="rule-number">7.</span>
          Еду и напитки можно приносить с собой или заказывать на месте
        </div>
      </div>

      <div className="welcome-action">
        <TavernButton
          variant="primary"
          fullWidth
          disabled={isLoading}
          onClick={handleAccept}
          className="accept-button"
        >
          {isLoading ? 'Загрузка...' : '✓ Принять и продолжить'}
        </TavernButton>
      </div>
      </>
      )}
      </div>
    </div>
  );
}

